import type { jsPDF } from 'jspdf';

import { wrapLabel } from '~/lib/purchase-orders/pdf-wrap';

import {
  type PurchaseOrder,
  type PurchaseOrderParty,
  buildPurchaseOrderFilename,
  calculatePurchaseOrderTotals,
  formatAmount,
  formatDate,
  formatQuantity,
  getVisibleLineItems,
  parseAmount,
  roundToCents,
} from './purchase-order';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const FOOTER_Y = PAGE_HEIGHT - 10;

const INK = [31, 27, 25] as const;
const MUTED = [122, 114, 108] as const;
const RULE = [214, 209, 203] as const;
const PANEL = [244, 242, 239] as const;
/** The ship-to panel, cool against the supplier panel's warm grey. */
const SHIP_PANEL = [234, 239, 244] as const;
const FREE_ROW = [248, 246, 242] as const;

function setInk(doc: jsPDF, colour: readonly [number, number, number]) {
  doc.setTextColor(colour[0], colour[1], colour[2]);
}

/** Draws a Title Case section label above a block. */
function drawLabel(doc: jsPDF, label: string, x: number, y: number) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setInk(doc, MUTED);
  doc.text(label, x, y);
}

function partyDetails(party: PurchaseOrderParty) {
  return [party.address, party.contact, party.email, party.phone ?? '']
    .map((value) => value.trim())
    .filter(Boolean)
    .join('\n');
}

/**
 * Renders one address block and returns the y coordinate directly below it, so
 * the caller can keep stacking content without tracking line counts itself.
 */
function drawParty(
  doc: jsPDF,
  party: PurchaseOrderParty,
  x: number,
  y: number,
  width: number,
) {
  let cursorY = y;

  if (party.name.trim()) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setInk(doc, INK);

    for (const line of doc.splitTextToSize(party.name.trim(), width)) {
      doc.text(line, x, cursorY);
      cursorY += 4.4;
    }
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setInk(doc, MUTED);

  const details = partyDetails(party);

  if (details) {
    for (const line of doc.splitTextToSize(details, width)) {
      doc.text(line, x, cursorY);
      cursorY += 3.9;
    }
  }

  return cursorY;
}

/**
 * Height `drawParty` will occupy for this content, measured with the same
 * fonts it draws in. The address panels are sized from this rather than from a
 * fixed height, so a long address does not spill out of the shaded box.
 */
function measurePartyHeight(
  doc: jsPDF,
  party: PurchaseOrderParty,
  width: number,
) {
  let height = 0;

  if (party.name.trim()) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    height += doc.splitTextToSize(party.name.trim(), width).length * 4.4;
  }

  const details = partyDetails(party);

  if (details) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    height += doc.splitTextToSize(details, width).length * 3.9;
  }

  return height;
}

function drawMetaRow(
  doc: jsPDF,
  label: string,
  value: string,
  right: number,
  y: number,
) {
  const shown = value.trim();

  if (!shown) return;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setInk(doc, MUTED);
  doc.text(label, right - 40, y, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  setInk(doc, INK);

  /*
   * Clamped to its own column. These values are right-aligned, so a long one
   * ran left out of the meta block and over the buyer's name.
   */
  const [clamped] = doc.splitTextToSize(shown, 36) as string[];

  doc.text(clamped ?? shown, right, y, { align: 'right' });
}

function lineDescription(item: { description: string; freeLabel?: string }) {
  const name = item.description.trim();
  const label = item.freeLabel?.trim() ?? '';

  if (name && label) return `${name}\n${label}`;

  return label || name;
}

function ensureRoom(doc: jsPDF, y: number, needed: number) {
  if (y + needed <= PAGE_HEIGHT - 22) return y;

  doc.addPage();

  return MARGIN + 8;
}

function drawWrappedText(
  doc: jsPDF,
  value: string,
  x: number,
  y: number,
  width: number,
  lineHeight: number,
  options?: { paginate?: boolean },
) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setInk(doc, INK);

  let cursorY = y;

  for (const line of doc.splitTextToSize(value, width) as string[]) {
    if (options?.paginate !== false) {
      cursorY = ensureRoom(doc, cursorY, lineHeight + 2);
    }

    doc.text(line, x, cursorY);
    cursorY += lineHeight;
  }

  return cursorY;
}

export async function generatePurchaseOrderPdf(
  order: PurchaseOrder,
  productName: string,
) {
  const [{ jsPDF: JsPdf }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc = new JsPdf({ format: 'a4', orientation: 'portrait', unit: 'mm' });
  const totals = calculatePurchaseOrderTotals(order);
  const currency = order.currency || 'USD';

  doc.setProperties({
    author: order.buyer.name.trim() || undefined,
    subject: `Purchase order ${order.poNumber.trim()}`,
    title: `Purchase Order ${order.poNumber.trim()}`.trim(),
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  setInk(doc, INK);
  doc.text('Purchase Order', MARGIN, 22);

  const buyerBottom = drawParty(doc, order.buyer, MARGIN, 30, 78);

  const metaRight = PAGE_WIDTH - MARGIN;
  let metaY = 18;
  const metaRows = [
    ['PO Number', order.poNumber.trim()],
    ['Version', order.version ? `v${order.version}` : ''],
    ['Order Name', order.orderName?.trim() ?? ''],
    ['Supplier Reference', order.supplierReference?.trim() ?? ''],
    ['Order Date', formatDate(order.orderDate)],
    [order.shipByLabel ?? 'Ship By', formatDate(order.shipByDate)],
    ['Expected Delivery', formatDate(order.expectedDeliveryDate ?? '')],
    ['Currency', currency],
  ] as const;

  for (const [label, value] of metaRows) {
    if (!value.trim()) continue;

    drawMetaRow(doc, label, value, metaRight, metaY);
    metaY += 5.2;
  }

  const headerBottom = Math.max(buyerBottom, metaY) + 3;

  doc.setDrawColor(RULE[0], RULE[1], RULE[2]);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, headerBottom, PAGE_WIDTH - MARGIN, headerBottom);

  const panelTop = headerBottom + 7;
  const panelGap = 6;
  const panelWidth = (CONTENT_WIDTH - panelGap) / 2;
  const panelInnerWidth = panelWidth - 10;
  const panelHeight = Math.max(
    24,
    12 +
      Math.max(
        measurePartyHeight(doc, order.supplier, panelInnerWidth),
        measurePartyHeight(doc, order.shipTo, panelInnerWidth),
      ),
  );

  doc.setFillColor(PANEL[0], PANEL[1], PANEL[2]);
  doc.roundedRect(MARGIN, panelTop, panelWidth, panelHeight, 1.5, 1.5, 'F');

  doc.setFillColor(SHIP_PANEL[0], SHIP_PANEL[1], SHIP_PANEL[2]);
  doc.roundedRect(
    MARGIN + panelWidth + panelGap,
    panelTop,
    panelWidth,
    panelHeight,
    1.5,
    1.5,
    'F',
  );

  drawLabel(doc, 'Supplier', MARGIN + 5, panelTop + 6);
  drawParty(doc, order.supplier, MARGIN + 5, panelTop + 12, panelInnerWidth);

  drawLabel(doc, 'Ship To', MARGIN + panelWidth + panelGap + 5, panelTop + 6);
  drawParty(
    doc,
    order.shipTo,
    MARGIN + panelWidth + panelGap + 5,
    panelTop + 12,
    panelInnerWidth,
  );

  const tableTop = panelTop + panelHeight + 8;
  const lineItems = getVisibleLineItems(order);
  const showHsCode = lineItems.some(({ item }) => item.hsCode.trim());
  const showUnit = lineItems.some(({ item }) => item.unit.trim());
  const skuWidth = showHsCode || showUnit ? 32 : 42;
  const priceWidth = showHsCode || showUnit ? 26 : 30;
  const lineTotalWidth = showHsCode || showUnit ? 28 : 32;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);

  const columns = [
    { header: '#', style: { cellWidth: 8, halign: 'center' as const } },
    { header: 'SKU', style: { cellWidth: skuWidth } },
    { header: 'Description', style: { cellWidth: 'auto' as const } },
    ...(showHsCode ? [{ header: 'HS Code', style: { cellWidth: 18 } }] : []),
    { header: 'Qty', style: { cellWidth: 16, halign: 'right' as const } },
    ...(showUnit ? [{ header: 'Unit', style: { cellWidth: 14 } }] : []),
    {
      header: `Unit Price (${currency})`,
      style: { cellWidth: priceWidth, halign: 'right' as const },
    },
    {
      header: `Line Total (${currency})`,
      style: { cellWidth: lineTotalWidth, halign: 'right' as const },
    },
  ];

  autoTable(doc, {
    body: lineItems.map(({ item, index }, row) => [
      String(row + 1),
      wrapLabel(item.sku.trim(), skuWidth - 5.4, (value) =>
        doc.getTextWidth(value),
      ),
      lineDescription(item),
      ...(showHsCode ? [item.hsCode.trim()] : []),
      formatQuantity(parseAmount(item.quantity)),
      ...(showUnit ? [item.unit.trim()] : []),
      formatAmount(parseAmount(item.unitPrice)),
      formatAmount(totals.lineTotals[index] ?? 0),
    ]),
    bodyStyles: {
      fontSize: 8.5,
      overflow: 'linebreak',
      textColor: [INK[0], INK[1], INK[2]],
      valign: 'top',
    },
    columnStyles: Object.fromEntries(
      columns.map((column, index) => [index, column.style]),
    ),
    didParseCell: (data) => {
      const header = columns[data.column.index]?.header ?? '';
      const numeric =
        header === 'Qty' ||
        header.startsWith('Unit Price') ||
        header.startsWith('Line Total');

      /*
       * Foot styles replace the column alignment, so the total row was
       * left-aligned under right-aligned figures. Put the totals back in
       * the same column edge as the rows above them.
       */
      if (data.section === 'foot' && numeric) {
        data.cell.styles.halign = 'right';
      }

      if (data.section === 'head' && numeric) {
        data.cell.styles.halign = 'right';
      }

      if (data.section !== 'body') return;

      const entry = lineItems[data.row.index];

      if (!entry?.item.freeLabel) return;

      data.cell.styles.fillColor = [FREE_ROW[0], FREE_ROW[1], FREE_ROW[2]];
    },
    foot: [
      columns.map((column) => {
        if (column.header === 'Qty') {
          return formatQuantity(totals.totalQuantity);
        }

        if (column.header.startsWith('Line Total')) {
          return formatAmount(totals.subtotal);
        }

        return column.header === 'Description' ? 'Total' : '';
      }),
    ],
    /*
     * Once, at the end. A foot row repeated on every page would show the
     * whole order's subtotal under a fraction of its lines.
     */
    showFoot: 'lastPage',
    footStyles: {
      fillColor: [PANEL[0], PANEL[1], PANEL[2]],
      fontSize: 8.5,
      fontStyle: 'bold',
      overflow: 'linebreak',
      textColor: [INK[0], INK[1], INK[2]],
    },
    head: [columns.map((column) => column.header)],
    headStyles: {
      fillColor: [INK[0], INK[1], INK[2]],
      fontSize: 7.5,
      fontStyle: 'bold',
      overflow: 'linebreak',
      textColor: [255, 255, 255],
      valign: 'middle',
    },
    margin: { bottom: 18, left: MARGIN, right: MARGIN, top: 16 },
    rowPageBreak: 'avoid',
    showHead: 'everyPage',
    startY: tableTop,
    styles: {
      cellPadding: { bottom: 2.2, left: 2.2, right: 2.2, top: 2.2 },
      font: 'helvetica',
      lineColor: [RULE[0], RULE[1], RULE[2]],
      lineWidth: 0.1,
      overflow: 'linebreak',
    },
    theme: 'grid',
  });

  const tableBottom =
    (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? tableTop;

  const payments = (order.payments ?? []).filter(
    (payment) => Number.isFinite(payment.amount) && payment.amount > 0,
  );
  const paidTotal = roundToCents(
    payments.reduce((sum, payment) => sum + payment.amount, 0),
  );
  const deposit = Math.min(
    roundToCents(Math.max(0, order.depositAmount ?? 0)),
    totals.total,
  );
  const showDeposit =
    order.showDepositSplit === false ? false : deposit > 0.009;
  const schedule = order.paymentScheduleParsed
    ? (order.paymentSchedule ?? [])
    : [];
  const paymentNotes = order.paymentNotes?.trim() ?? '';
  const incoterm = order.incoterm.trim();
  const rawTerms = order.paymentTerms.trim();

  const totalRows = [
    ['Subtotal', formatAmount(totals.subtotal)],
    ...(totals.discount > 0
      ? [['Discount', `-${formatAmount(totals.discount)}`]]
      : []),
    ...(totals.tax > 0
      ? [[`Tax (${totals.taxPercent}%)`, formatAmount(totals.tax)]]
      : []),
    ...(totals.shipping > 0
      ? [['Shipping', formatAmount(totals.shipping)]]
      : []),
    ...(totals.otherCharges > 0
      ? [['Other Charges', formatAmount(totals.otherCharges)]]
      : []),
    ...(showDeposit
      ? [
          ['Deposit (Per Terms)', formatAmount(deposit)],
          [
            'Balance (Per Terms)',
            formatAmount(roundToCents(totals.total - deposit)),
          ],
        ]
      : []),
    ...(paidTotal > 0 ? [['Paid To Date', `-${formatAmount(paidTotal)}`]] : []),
  ];

  const totalsBlockWidth = 84;
  const totalsLeft = PAGE_WIDTH - MARGIN - totalsBlockWidth + 6;
  const leftWidth = totalsLeft - MARGIN - 8;
  const totalsHeight = totalRows.length * 5.2 + 16;
  let cursorY = ensureRoom(
    doc,
    tableBottom + 8,
    Math.min(totalsHeight + 8, 70),
  );

  const sectionTop = cursorY;
  let leftY = sectionTop;

  if (incoterm) {
    drawLabel(doc, 'Incoterm', MARGIN, leftY);
    leftY += 4.6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    setInk(doc, INK);
    leftY = drawWrappedText(doc, incoterm, MARGIN, leftY, leftWidth, 4.2, {
      paginate: false,
    });
    leftY += 3;
  }

  if (schedule.length > 0) {
    drawLabel(doc, 'Payment Schedule', MARGIN, leftY);
    leftY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);

    for (const milestone of schedule) {
      const labelWidth = leftWidth - 28;
      const labelLines = doc.splitTextToSize(
        milestone.label,
        labelWidth,
      ) as string[];
      const rowHeight = Math.max(4.6, labelLines.length * 4);

      doc.setFont('helvetica', 'normal');
      setInk(doc, MUTED);

      labelLines.forEach((line, index) => {
        doc.text(line, MARGIN, leftY + index * 4);
      });

      doc.setFont('helvetica', 'bold');
      setInk(doc, INK);
      doc.text(formatAmount(milestone.amount), totalsLeft - 10, leftY, {
        align: 'right',
      });
      leftY += rowHeight;
    }

    leftY += 2;
  } else if (rawTerms) {
    drawLabel(doc, 'Payment Terms', MARGIN, leftY);
    leftY += 4.6;
    leftY = drawWrappedText(doc, rawTerms, MARGIN, leftY, leftWidth, 4, {
      paginate: false,
    });
    leftY += 2;
  }

  if (paymentNotes) {
    leftY += 1;
    leftY = drawWrappedText(doc, paymentNotes, MARGIN, leftY, leftWidth, 4, {
      paginate: false,
    });
  }

  let totalsY = sectionTop;

  for (const [label, value] of totalRows) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setInk(doc, MUTED);
    doc.text(label ?? '', totalsLeft, totalsY);

    doc.setFont('helvetica', 'bold');
    setInk(doc, INK);
    doc.text(value ?? '', PAGE_WIDTH - MARGIN, totalsY, { align: 'right' });
    totalsY += 5.2;
  }

  totalsY += 1.2;
  doc.setFillColor(INK[0], INK[1], INK[2]);
  doc.roundedRect(
    totalsLeft - 4,
    totalsY,
    totalsBlockWidth - 2,
    12,
    1.5,
    1.5,
    'F',
  );

  const outstanding = roundToCents(Math.max(0, totals.total - paidTotal));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(255, 255, 255);
  doc.text(
    paidTotal > 0 ? `Balance Due (${currency})` : `Total Due (${currency})`,
    totalsLeft,
    totalsY + 7.4,
  );
  doc.setFontSize(11);
  doc.text(formatAmount(outstanding), PAGE_WIDTH - MARGIN - 4, totalsY + 7.6, {
    align: 'right',
  });

  cursorY = Math.max(leftY, totalsY + 16) + 4;

  if (payments.length > 0) {
    cursorY = ensureRoom(doc, cursorY, payments.length * 4.6 + 12);
    drawLabel(doc, 'Payments Received', MARGIN, cursorY);
    cursorY += 5;

    for (const payment of payments) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      setInk(doc, MUTED);

      const described = [
        payment.label.trim() || 'Payment',
        formatDate(payment.date) || null,
        payment.method.trim() || null,
      ]
        .filter(Boolean)
        .join(', ');

      doc.text(described, MARGIN, cursorY);
      doc.setFont('helvetica', 'bold');
      setInk(doc, INK);
      doc.text(formatAmount(payment.amount), PAGE_WIDTH - MARGIN, cursorY, {
        align: 'right',
      });
      cursorY += 4.6;
    }

    cursorY += 4;
  }

  const paymentDetails = (order.paymentDetails ?? [])
    .map((line) => line.trim())
    .filter(Boolean);

  if (paymentDetails.length > 0) {
    cursorY = ensureRoom(doc, cursorY, paymentDetails.length * 4.6 + 12);
    drawLabel(doc, 'Payment Details', MARGIN, cursorY);
    cursorY += 4.6;

    for (const line of paymentDetails) {
      cursorY = drawWrappedText(doc, line, MARGIN, cursorY, CONTENT_WIDTH, 4);
      cursorY += 1.5;
    }

    cursorY += 2.5;
  }

  const noteBlocks = [
    ['Notes', order.notes.trim()],
    ['Supplier Instructions', order.supplierInstructions?.trim() ?? ''],
  ] as const;

  for (const [label, value] of noteBlocks) {
    if (!value) continue;

    cursorY = ensureRoom(doc, cursorY, 16);
    drawLabel(doc, label, MARGIN, cursorY);
    cursorY += 4.6;
    cursorY = drawWrappedText(doc, value, MARGIN, cursorY, CONTENT_WIDTH, 4);
    cursorY += 4;
  }

  cursorY = ensureRoom(doc, cursorY, 18);
  doc.setDrawColor(RULE[0], RULE[1], RULE[2]);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, cursorY + 8, MARGIN + 62, cursorY + 8);
  drawLabel(doc, 'Authorised Signature', MARGIN, cursorY + 12.5);
  doc.line(
    PAGE_WIDTH - MARGIN - 62,
    cursorY + 8,
    PAGE_WIDTH - MARGIN,
    cursorY + 8,
  );
  drawLabel(doc, 'Date', PAGE_WIDTH - MARGIN - 62, cursorY + 12.5);

  const pageCount = doc.getNumberOfPages();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setInk(doc, MUTED);
    doc.text(
      `${order.poNumber.trim() || 'Purchase Order'}. Created with ${productName}`,
      MARGIN,
      FOOTER_Y,
    );
    doc.text(`Page ${page} of ${pageCount}`, PAGE_WIDTH - MARGIN, FOOTER_Y, {
      align: 'right',
    });
  }

  return {
    blob: doc.output('blob') as Blob,
    filename: buildPurchaseOrderFilename(order.poNumber),
  };
}

export async function downloadPurchaseOrderPdf(
  order: PurchaseOrder,
  productName: string,
) {
  const { blob, filename } = await generatePurchaseOrderPdf(order, productName);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
